import React, { useState, useMemo } from 'react';
import { MemberAvatar } from 'app/src/components/MemberAvatar';
import styles from './ChooseMember.less';
import Icon, { IconName } from 'app/common/components/controls/icon';
import { forTemplate, localizeCount } from 'app/common/lib/util/i18n';
import { buildFuzzyMatcher } from 'app/gamma/src/selectors/boards';
import { ComponentWrapper } from 'app/src/components/ComponentWrapper';
import { Button } from '@trello/nachos';
import { Key, getKey } from '@trello/keybindings';
import cx from 'classnames';
import { ChecklistTestIds } from '@trello/test-ids';

const format = forTemplate('choose_member');

interface Member {
  id: string;
  fullName: string;
}

type MemberGetter<M extends Member> = () => M[];

interface ChooseMemberProps<M extends Member> {
  idMemberInitial?: string | null;
  getCardMembers: MemberGetter<M>;
  getBoardMembers: MemberGetter<M>;
  onSelect: (member: M | null) => void;
  testId?: ChecklistTestIds;
}

interface MemberRowProps {
  member: Member;
  selected: boolean;
  highlight: boolean;
  onClick: () => void;
  onFocus: () => void;
  onMouseOver: () => void;
}

const MAX_MEMBERS_IN_SECTION = 12;

const MemberRow: React.FunctionComponent<MemberRowProps> = ({
  highlight,
  member,
  onClick,
  onFocus,
  onMouseOver,
  selected,
}) => {
  return (
    <li>
      <button
        className={cx(styles.memberButton, { [styles.highlight]: highlight })}
        onMouseOver={onMouseOver}
        onFocus={onFocus}
        onClick={onClick}
      >
        <MemberAvatar className={styles.memberAvatar} idMember={member.id} />
        <div className={styles.memberName}>{member.fullName}</div>
        {selected && <Icon name={IconName.Check} />}
      </button>
    </li>
  );
};

function useFilterMembers<M extends Member>(
  search: string,
  getCardMembers: MemberGetter<M>,
  getBoardMembers: MemberGetter<M>,
) {
  return useMemo(() => {
    const matcher = buildFuzzyMatcher(search);
    const isMatch = (member: Member) => matcher(member.fullName);

    const cardMembers = getCardMembers().filter(isMatch);

    const cardMemberIdSet = new Set(cardMembers.map((member) => member.id));

    const boardMembers = getBoardMembers().filter(
      (member) => isMatch(member) && !cardMemberIdSet.has(member.id),
    );

    return { cardMembers, boardMembers };
  }, [search, getCardMembers, getBoardMembers]);
}

function adjacentMember<M extends Member>(
  members: M[],
  current: M | undefined,
  direction: 'up' | 'down',
): M | undefined {
  if (members.length === 0 || !current) {
    return undefined;
  }

  const indexCurrent = members.indexOf(current);
  const indexNext = indexCurrent + (direction === 'up' ? -1 : 1);

  // Wrap around if we reach the top/bottom
  return members[(members.length + indexNext) % members.length];
}

export function ChooseMember<M extends Member>(props: ChooseMemberProps<M>) {
  const { idMemberInitial, onSelect } = props;

  const [searching, setSearching] = useState(true);
  const [search, setSearch] = useState('');

  const { cardMembers, boardMembers } = useFilterMembers(
    search,
    props.getCardMembers,
    props.getBoardMembers,
  );

  const allMatchingMembers = [...cardMembers, ...boardMembers];

  const defaultMember =
    allMatchingMembers.length > 0 ? allMatchingMembers[0] : undefined;

  const [manualMember, setManualMember] = useState(defaultMember);

  const effectiveMember = searching
    ? manualMember && allMatchingMembers.includes(manualMember)
      ? manualMember
      : defaultMember
    : undefined;

  const toggleMember = (member: M | undefined) =>
    member && onSelect(member.id === idMemberInitial ? null : member);

  const onKeyboardNavigation = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    switch (getKey(event)) {
      case Key.ArrowDown:
        setManualMember(
          adjacentMember(allMatchingMembers, effectiveMember, 'down'),
        );
        break;

      case Key.ArrowUp:
        setManualMember(
          adjacentMember(allMatchingMembers, effectiveMember, 'up'),
        );
        break;

      case Key.Enter:
        toggleMember(effectiveMember);
        break;

      default:
        return;
    }

    event.preventDefault();
  };

  const renderMembers = (members: M[]) => (
    <>
      <ul className={styles.memberList}>
        {members.slice(0, MAX_MEMBERS_IN_SECTION).map((member) => {
          const selected = member.id === idMemberInitial;

          return (
            <MemberRow
              key={member.id}
              member={member}
              selected={selected}
              highlight={member === effectiveMember}
              onClick={() => toggleMember(member)}
              onFocus={() => setManualMember(undefined)}
              onMouseOver={() => {
                if (searching) {
                  setManualMember(member);
                }
              }}
            />
          );
        })}
      </ul>
      {members.length > MAX_MEMBERS_IN_SECTION && (
        <div className={styles.memberOverflow}>
          <div>
            {localizeCount(
              'hiding-additional-members',
              members.length - MAX_MEMBERS_IN_SECTION,
            )}
          </div>
          <div>{format('search-to-find-more')}</div>
        </div>
      )}
    </>
  );

  return (
    <ComponentWrapper>
      <input
        autoFocus
        className="js-autofocus"
        type="text"
        placeholder={format('search-members')}
        onChange={(e) => {
          setSearch(e.target.value);
          setManualMember(undefined);
        }}
        onKeyDown={onKeyboardNavigation}
        onBlur={() => setSearching(false)}
        onFocus={() => setSearching(true)}
      />
      {cardMembers.length > 0 && (
        <>
          <h4 className={styles.heading}>{format('card-members')}</h4>
          {renderMembers(cardMembers)}
        </>
      )}

      {boardMembers.length > 0 && (
        <>
          <h4 className={styles.heading}>{format('other-board-members')}</h4>
          {renderMembers(boardMembers)}
        </>
      )}
      {}
      <Button
        className={styles.removeButton}
        onClick={() => {
          onSelect(null);
        }}
        shouldFitContainer={true}
        isDisabled={!idMemberInitial}
        data-test-id={ChecklistTestIds.ChecklistItemRemoveMemberButton}
      >
        {format('remove-member')}
      </Button>
    </ComponentWrapper>
  );
}
